import React from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  Download,
  Eye,
  FileText,
  Home,
  Mail,
  Phone,
  UserRound,
  Upload,
} from "lucide-react";
import "./RenovationProjectPropertyInformationStep.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

function findStep(steps = []) {
  return (
    steps.find((step) => step.step_code === "PROPERTY_INFORMATION") || {
      step_code: "PROPERTY_INFORMATION",

      step_order: 1,

      status: "NOT_STARTED",
    }
  );
}

function valueOrDash(value) {
  return value || "—";
}

function formatEnum(value) {
  if (!value) {
    return "—";
  }

  return String(value)
    .replaceAll("_", " ")

    .toLowerCase()

    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatSurface(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  return `${Number(value).toFixed(2)} m²`;
}

const COUNTRY_LABELS = {
  BE: "Belgium",

  ES: "Spain",

  NL: "Netherlands",

  FR: "France",
};

function formatCountry(value) {
  return COUNTRY_LABELS[value] || value || "—";
}

export default function RenovationProjectPropertyInformationStep() {
  const { t } = useTranslation("oss-project-property-information");

  const navigate = useNavigate();

  const {
    project,
    steps,
    basePath,
    registerStepSaveHandler,
    setStepDirty,
    isStepSaving,
  } = useOutletContext();

  const { getAccessTokenSilently } = useAuth0();

  const epcInputRef = React.useRef(null);

  const [selectedEpcFile, setSelectedEpcFile] = React.useState(null);

  const [uploadedEpc, setUploadedEpc] = React.useState(null);

  const [epcUploadError, setEpcUploadError] = React.useState("");

  const step = findStep(steps);

  const projectStatus = project?.status || "PLANNING";

  const statusClass = String(projectStatus).toLowerCase().replaceAll("_", "-");

  const property = project?.property || {};

  const homeowner = project?.homeowner || {};

  const oss = project?.oss || project?.oss_organisation || {};

  const homeownerPhoto =
    homeowner.profile_image_url || homeowner.image_url || "";

  const epcFile =
    uploadedEpc ||
    property.epc_document ||
    property.epc_file ||
    property.energy_performance_certificate ||
    null;

  const epcViewUrl = epcFile?.file_url || epcFile?.view_url || "";

  const epcDownloadUrl = epcFile?.download_url || epcFile?.file_url || "";

  function handleEpcFileChange(event) {
    const file = event.target.files?.[0] || null;

    setEpcUploadError("");

    if (!file) {
      setSelectedEpcFile(null);

      setStepDirty(false);

      return;
    }

    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");

    if (!isPdf) {
      setSelectedEpcFile(null);

      setStepDirty(false);

      setEpcUploadError(
        t(
          "propertyInfo.epcUploadPdfOnly",

          "Please select a PDF file.",
        ),
      );

      event.target.value = "";

      return;
    }

    const maxSizeBytes = 20 * 1024 * 1024;

    if (file.size > maxSizeBytes) {
      setSelectedEpcFile(null);

      setStepDirty(false);

      setEpcUploadError(
        t(
          "propertyInfo.epcUploadTooLarge",

          "The EPC file must be smaller than 20 MB.",
        ),
      );

      event.target.value = "";

      return;
    }

    setSelectedEpcFile(file);

    setStepDirty(true);
  }

  const savePropertyInformation = React.useCallback(async () => {
    if (!selectedEpcFile) {
      return;
    }

    const token = await getAccessTokenSilently();

    const formData = new FormData();

    formData.append("file", selectedEpcFile);

    const response = await fetch(
      `${API_BASE}/api/projects/${project.id}/initial-epc/`,

      {
        method: "POST",

        headers: {
          Authorization: `Bearer ${token}`,
        },

        body: formData,
      },
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        data?.detail ||
          data?.file?.[0] ||
          t(
            "propertyInfo.epcUploadFailed",

            "Could not save the EPC document.",
          ),
      );
    }

    const newEpc = data?.epc_document || data;

    setUploadedEpc(newEpc);

    setSelectedEpcFile(null);

    setEpcUploadError("");

    if (epcInputRef.current) {
      epcInputRef.current.value = "";
    }
  }, [getAccessTokenSilently, project?.id, selectedEpcFile, t]);

  React.useEffect(() => {
    if (!registerStepSaveHandler) {
      return undefined;
    }

    return registerStepSaveHandler(savePropertyInformation);
  }, [registerStepSaveHandler, savePropertyInformation]);

  const goNext = () => {
    navigate(`${basePath}/${project.id}/pre-assessment`);
  };

  async function openProtectedFile(url, filename, download = false) {
    if (!url) {
      return;
    }

    const token = await getAccessTokenSilently();

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error("Could not open file.");
    }

    const blob = await response.blob();

    const blobUrl = URL.createObjectURL(blob);

    if (download) {
      const link = document.createElement("a");

      link.href = blobUrl;

      link.download = filename || "document.pdf";

      document.body.appendChild(link);

      link.click();

      link.remove();

      URL.revokeObjectURL(blobUrl);

      return;
    }

    window.open(blobUrl, "_blank", "noopener,noreferrer");

    window.setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 60000);
  }

  return (
    <article className="renovation-property-step">
      {/* Header */}

      <header className="renovation-property-step__header">
        <div>
          <span className="renovation-property-step__eyebrow">
            <Home size={15} />

            {t("propertyInfo.eyebrow")}
          </span>

          <h1>{t("propertyInfo.title")}</h1>

          <p>{t("propertyInfo.subtitle")}</p>
        </div>

        <div className="renovation-property-step__status">
          <span className="renovation-property-step__stage-count">
            {t("propertyInfo.stage", {
              current: step.step_order || 1,

              total: 8,
            })}
          </span>

          <span
            className={
              `renovation-property-step__status-pill ` +
              `renovation-property-step__status-pill--${statusClass}`
            }
          >
            {t(`projectStatuses.${projectStatus}`, projectStatus)}
          </span>
        </div>
      </header>

      {/* Property */}

      <section className="renovation-property-section">
        <div className="renovation-property-section__title">
          <Building2 size={18} />

          <h2>{t("propertyInfo.propertyDetails")}</h2>
        </div>

        <div className="renovation-property-grid">
          <label>
            {t("fields.propertyAddress")}

            <input value={valueOrDash(property.full_address)} readOnly />
          </label>

          <label>
            {t("fields.city")}

            <input value={valueOrDash(property.city)} readOnly />
          </label>

          <label>
            {t("fields.postcode")}

            <input value={valueOrDash(property.postal_code)} readOnly />
          </label>

          <label>
            {t("fields.country")}

            <input value={formatCountry(property.country)} readOnly />
          </label>

          <label>
            {t("fields.region")}

            <input value={formatEnum(property.region)} readOnly />
          </label>

          <label>
            {t("fields.propertyType")}

            <input value={formatEnum(property.property_type)} readOnly />
          </label>

          <label>
            {t("fields.buildingCondition")}

            <input value={formatEnum(property.building_condition)} readOnly />
          </label>

          <label>
            {t("fields.surface")}

            <input value={formatSurface(property.surface_sqm)} readOnly />
          </label>

          <label>
            {t("fields.currentEpc")}

            <input value={valueOrDash(property.epc_rating_current)} readOnly />
          </label>

          <label>
            {t("fields.targetEpc")}

            <input value={valueOrDash(property.epc_rating_target)} readOnly />
          </label>
        </div>

        <div
          className={`renovation-property-file ${
            !epcFile ? "renovation-property-file--missing" : ""
          }`}
        >
          <div className="renovation-property-file__identity">
            <FileText size={18} />

            <div>
              <strong>{t("propertyInfo.epcFile")}</strong>

              <span>
                {epcFile?.filename ||
                  epcFile?.original_filename ||
                  t("propertyInfo.noEpcFile")}
              </span>
            </div>
          </div>

          {epcFile ? (
            <div className="renovation-property-file__actions">
              {epcViewUrl && (
                <button
                  type="button"
                  className="renovation-property-icon-btn"
                  onClick={() =>
                    openProtectedFile(
                      epcViewUrl,

                      epcFile?.filename || epcFile?.original_filename,

                      false,
                    )
                  }
                  title={t("buttons.viewFile")}
                  aria-label={t("buttons.viewFile")}
                >
                  <Eye size={17} />
                </button>
              )}

              {epcDownloadUrl && (
                <button
                  type="button"
                  className="renovation-property-icon-btn"
                  onClick={() =>
                    openProtectedFile(
                      epcDownloadUrl,

                      epcFile?.filename || epcFile?.original_filename,

                      true,
                    )
                  }
                  title={t("buttons.downloadFile")}
                  aria-label={t("buttons.downloadFile")}
                >
                  <Download size={17} />
                </button>
              )}
            </div>
          ) : (
            <div className="renovation-property-epc-upload">
              <div className="renovation-property-epc-upload__copy">
                <strong>
                  {t(
                    "propertyInfo.epcUploadTitle",

                    "No EPC document has been added yet.",
                  )}
                </strong>

                <span>
                  {t(
                    "propertyInfo.epcUploadHelp",

                    "The Homebuyer or assigned One-Stop Shop can upload the current Energy Performance Certificate.",
                  )}
                </span>
              </div>

              <input
                ref={epcInputRef}
                type="file"
                accept=".pdf,application/pdf"
                className="renovation-property-epc-upload__input"
                onChange={handleEpcFileChange}
              />

              {selectedEpcFile ? (
                <div className="renovation-property-epc-upload__selected">
                  <FileText size={16} />

                  <span>{selectedEpcFile.name}</span>
                </div>
              ) : null}

              <div className="renovation-property-epc-upload__actions">
                <button
                  type="button"
                  className="renovation-property-epc-upload__choose"
                  onClick={() => epcInputRef.current?.click()}
                  disabled={isStepSaving}
                >
                  <Upload size={16} />

                  {selectedEpcFile
                    ? t("buttons.changeFile", "Change file")
                    : t("buttons.chooseEpc", "Choose EPC PDF")}
                </button>
              </div>

              {selectedEpcFile ? (
                <p className="renovation-property-epc-upload__pending">
                  {t(
                    "propertyInfo.epcPendingSave",

                    "EPC selected. Click Save changes at the top to save it.",
                  )}
                </p>
              ) : null}

              {epcUploadError ? (
                <p className="renovation-property-epc-upload__message renovation-property-epc-upload__message--error">
                  {epcUploadError}
                </p>
              ) : null}
            </div>
          )}
        </div>
      </section>

      {/* Homebuyer */}

      <section className="renovation-property-section">
        <div className="renovation-property-section__title">
          <UserRound size={18} />

          <h2>{t("propertyInfo.homeownerContact")}</h2>
        </div>

        <div className="renovation-property-owner">
          <div className="renovation-property-owner__avatar">
            {homeownerPhoto ? (
              <img src={homeownerPhoto} alt={homeowner.full_name || ""} />
            ) : (
              <UserRound size={28} />
            )}
          </div>

          <div className="renovation-property-owner__info">
            <strong>{valueOrDash(homeowner.full_name)}</strong>

            <span>
              <Mail size={15} />

              {valueOrDash(homeowner.email)}
            </span>

            <span>
              <Phone size={15} />

              {valueOrDash(homeowner.phone)}
            </span>
          </div>
        </div>
      </section>

      {/* OSS */}

      <section className="renovation-property-section">
        <div className="renovation-property-section__title">
          <BadgeCheck size={18} />

          <h2>{t("propertyInfo.ossContact")}</h2>
        </div>

        <div className="renovation-property-grid">
          <label>
            {t("fields.ossOrganisation")}

            <input
              value={valueOrDash(
                oss.organisation_name || oss.name || project.oss_name,
              )}
              readOnly
            />
          </label>

          <label>
            {t("fields.ossContactName")}

            <input
              value={valueOrDash(
                oss.contact_name || oss.representative_name || oss.name,
              )}
              readOnly
            />
          </label>

          <label>
            {t("fields.ossEmail")}

            <input
              value={valueOrDash(oss.contact_email || oss.email)}
              readOnly
            />
          </label>

          <label>
            {t("fields.ossPhone")}

            <input
              value={valueOrDash(oss.contact_phone || oss.phone)}
              readOnly
            />
          </label>
        </div>
      </section>

      {/* Next */}

      <button
        className="renovation-property-next"
        type="button"
        onClick={goNext}
      >
        {t("buttons.continuePreAssessment")}

        <ArrowRight size={17} />
      </button>
    </article>
  );
}
